import React from 'react'
import Image from 'next/image'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiClock } from '@react-icons/all-files/fi/FiClock'
import { FiStar } from '@react-icons/all-files/fi/FiStar'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import Action from '../primitives/Action'
import Container from '../foundations/Container'
import SeoHero from './SeoHero'
import styles from './HomepageHero.module.scss'

const clientLogos = [
  { name: 'VISA', src: '/Pictures/clients/visa.svg' },
  { name: 'Google', src: '/Pictures/clients/google.svg' },
  { name: 'Netflix', src: '/Pictures/clients/netflix.svg' },
  { name: 'Nike', src: '/Pictures/clients/nike.svg' },
  { name: 'Ferrari', src: '/Pictures/clients/ferrari.svg' },
  { name: 'Bentley', src: '/Pictures/clients/bentley.svg', wideOnly: true },
  { name: 'Boeing', src: '/Pictures/clients/boeing.svg', wideOnly: true },
  {
    name: 'Norwegian Cruise Line',
    src: '/Pictures/clients/norwegian-cruise-line.svg',
    wideOnly: true,
  },
  { name: 'Heineken', src: '/Pictures/clients/heineken.svg', wideOnly: true },
  { name: 'Humana', src: '/Pictures/clients/humana.svg', wideOnly: true },
  {
    name: 'Chewy',
    src: '/Pictures/clients/chewy.svg',
    wideOnly: true,
    preserveDetail: true,
  },
  {
    name: 'Harley-Davidson',
    src: '/Pictures/clients/harley-davidson.svg',
    wideOnly: true,
    preserveDetail: true,
  },
  { name: 'YouTube', src: '/Pictures/clients/youtube.svg' },
]

const HomepageHero = ({
  actions = [],
  contactAction,
  content,
  image,
  reviewData,
  secondaryActions = [],
  totalEvents,
}) => {
  const reviewSource = reviewData?.source
  const metrics = [
    {
      Icon: FiCalendar,
      label: 'Events',
      value: totalEvents,
      detail: `Serviced in ${new Date().getFullYear()}`,
      href: '#recent-events',
    },
    {
      Icon: FiStar,
      label: 'Satisfaction',
      value: reviewSource?.averageRating ? `${reviewSource.averageRating}/5` : 'Google',
      detail: reviewSource?.totalReviewCount
        ? `${reviewSource.totalReviewCount.toLocaleString('en-US')} Google reviews`
        : 'Verified Google reviews',
      href: '#homepage-client-reviews',
    },
    {
      Icon: FiUsers,
      label: 'Experience',
      value: '10+',
      detail: 'Years AV professionals',
      href: '#homepage-experience-proof-heading',
    },
    {
      Icon: FiClock,
      label: 'Availability',
      value: '24/7',
      detail: 'Delivery and Installation',
      href: '#homepage-business-info',
    },
  ]

  return (
    <SeoHero
      surface='dark'
      className={styles.hero}
      media={{
        src: image,
        alt: content?.imageAlt || 'Miami Sound Rental technicians operating a corporate event production',
        objectPosition: 'center'
      }}
      mediaClassName={styles.media}
      scrimClassName={styles.scrim}
    >

      <Container width='wide' className={styles.inner}>
        <div className={styles.stage}>
          <div className={styles.copy} data-homepage-hero-content>
            {content?.eyebrow && <span className={styles.eyebrow}>{content.eyebrow}</span>}
            <h1>{content?.title}</h1>
            {content?.subtitle && <h2>{content.subtitle}</h2>}
            {content?.paragraph && <p>{content.paragraph}</p>}

            {actions.length > 0 && (
              <div className={styles.actions}>
                {actions.map(action => (
                  <Action
                    {...action}
                    key={action.label}
                    icon={action.icon}
                    size='large'
                    variant={action.style === 'primary' ? 'primary' : 'dark'}
                  >
                    {action.label}
                  </Action>
                ))}
              </div>
            )}

            {contactAction && (
              <Action
                {...contactAction}
                icon={contactAction.icon}
                variant='plain'
                className={styles.contactLink}
              >
                {contactAction.label}
              </Action>
            )}

            {secondaryActions.length > 0 && (
              <nav className={styles.serviceNav} aria-label='Pricing and rental options'>
                <span className={styles.serviceNavLabel}>Prefer to explore pricing yourself?</span>
                <div className={styles.serviceNavActions}>
                  {secondaryActions.map(action => (
                    <Action
                      {...action}
                      href={action.href}
                      icon={action.icon}
                      prefetch={false}
                      variant='plain'
                      className={styles.serviceLink}
                      key={action.label}
                    >
                      {action.label}
                    </Action>
                  ))}
                </div>
              </nav>
            )}
          </div>
        </div>

        <div className={styles.proof}>
          <div className={styles.metrics} aria-label='Miami Sound Rental service metrics'>
            {metrics.map(({ Icon, label, value, detail, href }) => (
              <a className={styles.metric} href={href} key={label}>
                <span className={styles.metricIcon} aria-hidden='true'>
                  <Icon />
                </span>
                <span className={styles.metricCopy}>
                  <span className={styles.metricLabel}>{label}</span>
                  <strong>{value}</strong>
                  <span className={styles.metricDetail}>{detail}</span>
                </span>
              </a>
            ))}
          </div>

          <section className={styles.clients} aria-label='Selected Miami Sound Rental clients'>
            <span className={styles.clientTitle}>Trusted by teams from</span>
            <div className={styles.clientGrid}>
              {clientLogos.map(client => (
                <div
                  className={`${styles.clientLogo} ${client.wideOnly ? styles.wideClientLogo : ''} ${client.preserveDetail ? styles.detailedClientLogo : ''}`.trim()}
                  key={client.name}
                >
                  <Image
                    src={client.src}
                    alt={`${client.name} logo`}
                    width={180}
                    height={72}
                    unoptimized
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </section>
        </div>
      </Container>
    </SeoHero>
  )
}

export default HomepageHero
